import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Briefcase, Users, TrendingUp, ArrowUpRight, ArrowRight, CheckCircle2,
} from "lucide-react";
import { C, Pill } from "./shared";

const PROMISES = [
  [ShieldCheck, "Verified Credentials", C.navy],
  [Briefcase, "Better Opportunities", C.green],
  [Users, "Top Recruiters", C.orange],
  [TrendingUp, "Career Outcomes", C.purple],
];

const JOURNEY_LINES = [
  "Create your profile in under 2 minutes",
  "Verify skills with proctored assessments",
  "Discover internships & jobs that match",
  "Get placed at India's top companies",
];

export default function StudentsHero() {
  return (
    <section className="relative overflow-hidden" style={{ background: C.paper }}>
      <div className="absolute inset-0 -z-10 opacity-60"
        style={{ background: `radial-gradient(60% 50% at 90% 0%, ${C.green}18 0%, transparent 60%), radial-gradient(60% 50% at 0% 100%, ${C.navy}12 0%, transparent 60%)` }} />
      <div className="max-w-7xl mx-auto px-6 py-14 md:py-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <Pill color={C.green}>For Students</Pill>
          <h1 className="font-display font-black mt-5 leading-[1.05] text-4xl sm:text-5xl lg:text-6xl tracking-tight" style={{ color: C.navyDeep }}>
            Build <span style={{ color: C.green }}>Verified Skills.</span><br />
            Get Hired <span style={{ color: C.green }}>Faster.</span>
          </h1>
          <p className="text-slate-600 mt-5 text-lg max-w-xl leading-relaxed">
            The all-in-one platform that helps students verify skills, discover opportunities, and launch a successful career.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/signup?role=student" data-testid="hero-cta-create"
              className="px-6 py-3.5 rounded-lg font-semibold text-white inline-flex items-center gap-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              style={{ background: `linear-gradient(135deg, ${C.green} 0%, ${C.greenDeep} 100%)` }}>
              Create your profile <ArrowUpRight size={16} />
            </Link>
            <Link to="/opportunities" data-testid="hero-cta-explore"
              className="px-6 py-3.5 rounded-lg font-semibold inline-flex items-center gap-2 border-2 transition-all duration-300 hover:-translate-y-1"
              style={{ borderColor: C.navy, color: C.navy }}>
              Explore opportunities <ArrowRight size={16} />
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {PROMISES.map(([Icon, label, color], i) => (
              <div key={label} className="group flex items-center gap-2.5" data-testid={`promise-${i}`}>
                <div className="w-10 h-10 rounded-lg grid place-items-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3"
                  style={{ background: `${color}15`, color }}>
                  <Icon size={18} />
                </div>
                <span className="font-semibold text-xs md:text-sm leading-tight" style={{ color: C.ink }}>{label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="rounded-2xl p-7 text-white shadow-2xl relative overflow-hidden"
            style={{ background: `linear-gradient(160deg, ${C.navyDeep} 0%, ${C.navy} 100%)` }}>
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl opacity-30"
              style={{ background: C.green }} />
            <div className="relative">
              <div className="font-mono text-[11px] tracking-widest uppercase opacity-80" style={{ color: C.green }}>Start Your Journey</div>
              <h2 className="font-display font-black text-2xl md:text-3xl mt-1 leading-tight">Today is the day.</h2>
              <ul className="mt-6 space-y-3.5 text-sm">
                {JOURNEY_LINES.map((line, i) => (
                  <li key={line} className="flex items-start gap-3" data-testid={`journey-${i}`}>
                    <span className="mt-0.5 w-5 h-5 rounded-full grid place-items-center"
                      style={{ background: `${C.green}30`, color: C.green }}>
                      <CheckCircle2 size={14} />
                    </span>
                    <span className="text-white/90 leading-snug">{line}</span>
                  </li>
                ))}
              </ul>
              <Link to="/signup?role=student" data-testid="journey-cta"
                className="mt-7 w-full justify-center px-5 py-3.5 rounded-lg font-bold text-white inline-flex items-center gap-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                style={{ background: C.green }}>
                Create Your Profile <ArrowUpRight size={16} />
              </Link>
              <div className="mt-4 text-xs text-white/60 text-center">No credit card required.</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
